import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { site } from "@/content";

const { gaMeasurementId, clarityProjectId } = site.analytics;

const analyticsScripts = import.meta.env.PROD
  ? [
      { src: `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`, async: true },
      {
        children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaMeasurementId}');`,
      },
      {
        children: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${clarityProjectId}");`,
      },
    ]
  : [];

function NotFoundComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <div className="horizon-field flex min-h-[calc(100vh-4.5rem)] items-center px-6 py-16 text-white">
      <div className="relative z-10 mx-auto max-w-2xl text-center">
        <p className="section-tag section-tag--on-dark mx-auto w-fit">Page not found</p>
        <h1 className="mt-5 font-serif text-4xl leading-[1.1] sm:text-5xl">
          This page has moved on to its next era.
        </h1>
        <p className="mx-auto mt-5 max-w-md leading-relaxed text-white/70">
          <span className="truncate font-mono text-sm text-white/50">{pathname}</span> doesn't
          exist here anymore. Nothing else on the site is affected.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <Link to="/" className="btn-on-dark">
            Back to the homepage
          </Link>
          <Link to="/frequently-asked-questions" className="btn-ghost-dark">
            Or try the FAQ
          </Link>
        </div>
        <p className="mt-8 text-xs text-white/50">
          Tip: press{" "}
          <kbd className="rounded border border-white/20 bg-white/5 px-1.5 py-0.5 font-mono text-[11px]">
            Ctrl
          </kbd>{" "}
          <kbd className="rounded border border-white/20 bg-white/5 px-1.5 py-0.5 font-mono text-[11px]">
            K
          </kbd>{" "}
          to search the whole site instead.
        </p>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "NextEra Bookkeeping" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "NextEra Bookkeeping" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Source+Serif+4:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Inter:wght@400;500;600;700&display=swap",
      },
    ],
    scripts: analyticsScripts,
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en-AU">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function MobileBookingBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [visible, setVisible] = useState(false);

  // Only appear once the visitor has scrolled past the hero, so the bar
  // never stacks on top of the page's own calls to action.
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // The booking page already leads with the calendar; the bar would just cover the form.
  if (pathname === "/strategic-finance-review") return null;
  return (
    <div
      className={`mobile-bar fixed inset-x-0 bottom-0 z-40 border-t border-[color:var(--mist)] bg-white/95 p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] backdrop-blur lg:hidden ${
        visible ? "is-visible" : ""
      }`}
    >
      <Link to="/strategic-finance-review" className="btn-primary w-full">
        Book a Strategic Finance Review
      </Link>
    </div>
  );
}

function RootComponent() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
      </main>
      <SiteFooter />
      <MobileBookingBar />
    </div>
  );
}
